import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../utils/api';
import type { AwCtx } from '../../ctx';
import { baht, card, dmy, errBox, note, num, sectionHead, table, td, th } from '../../ui';
import { fmtDateTime } from '../../../../utils/region';
import { Dialog, Foot, Loader, field, isoDate, lbl } from '../../dialog';
import type { FaAsset, FaCategory, FaDetail, FaSource, FaSourceLine, FaStatus } from './shared';
import { STATUS_KEY, canWriteIn, isReadOnlySeat, toInput, toSatang } from './shared';
import { CloseBtn, FaAcct, SetByHand, acctLabel, fmtNum, pickName } from './AssetActions';
import { METHOD_KEY } from './AssetWindow';

// ── Register or correct an asset, full screen (batch 9 R4 — COA-H3 / H4 / H5 / H7 / H15 / H16) ──
// One form for three doors:
//   · NEW (assetId ''/'new'): the category comes first and is required (COA-H3 — "Each asset must be
//     assigned to an asset category, and the system should automatically retrieve the corresponding
//     COA mapping"); picking it fills the decree class, rate, life, method and the three accounts.
//     Any of them may then be changed for this one asset, and each such setting is marked "set by
//     hand" — the same comparison the server stores as `overrides`.
//   · BOUGHT ON AN EXPENSE RECORD (sourceKey): the record's lines as choices, each saying whether it
//     can carry an asset and why not (services/assetSource.ts); a line pre-fills the cost (what is
//     left of it), the name, supplier, invoice no. and date. ⛔ The asset must sit in the account the
//     purchase debited, so the categories that keep their assets there come first, and the form says
//     so in one sentence when the asset would sit elsewhere — and holds Register, which the server
//     would refuse (review R5 #2). Registering posts NOTHING: the purchase entry is the acquisition (SPEC:74).
//   · EDIT (assetId): only the fields that changed are sent — except that a NEW CATEGORY is sent with
//     everything a category gives (method, the three accounts, and the life or the rate) exactly as the
//     form holds it, because the server fills whatever such a patch leaves out from the new category:
//     an account set back to the old one, or a life typed after the pick, would otherwise be lost.
//     Once anything is charged, the fields the ledger computed from are locked and the form says why;
//     the descriptive ones stay open. A removed (soft-deleted) asset gets no form, only why.
//     ⛔ An asset BOUGHT on an Expense record keeps the asset account the purchase debited: PUT refuses
//     any other (review R4 #11, a 409 that pages the admins), so the form reads the line it was bought on
//     as updateAsset reads it, and the select is locked to THAT LINE's account and says why — a line
//     booked again since is followed (R5 #4). A category keeping its assets elsewhere does NOT hold Save:
//     the kept account is sent with it and accepted, marked "set by hand" (R5 #4 — R5 #2 held it, with a
//     sentence that was untrue here). A cost past what the line has left for it holds Save and says so;
//     a line that is gone locks the cost (R5 #5 — the line counts the cost alone, as at registration).
// ⛔ Amounts are typed in baht and SENT IN SATANG (toSatang); life and rate are one setting (100 / n),
// and only the one the operator typed is sent, so the server's agreement check never sees a rounding —
// working by life, never the rate box's 4-decimal 100 / n, not even as a fallback.
// ⛔ A rate taken from a category (or the asset) is held at FULL precision — a 3-year category is
// 33.333333%, and sending 33.3333 stored the asset off its category's rate, marked "set by hand".
// The company's charging frequency and the opening net book value are shown, never typed (Q6 / H15).
// Every refusal is the server's sentence, verbatim. `dirty` is set by the edit path only (FS-4).

type Form = {
  categoryId: string; nameTh: string; nameEn: string; acquiredDate: string; vendorName: string; vendorInvoiceNo: string;
  cost: string; capitalisedCost: string; availableDate: string; depreciationStart: string; life: string; rate: string; method: string;
  salvage: string; openingAccum: string; openingAsOf: string; location: string; status: string; note: string;
  assetAccountId: string; accumAccountId: string; expenseAccountId: string;
};
const BLANK: Form = {
  categoryId: '', nameTh: '', nameEn: '', acquiredDate: '', vendorName: '', vendorInvoiceNo: '', cost: '', capitalisedCost: '',
  availableDate: '', depreciationStart: '', life: '', rate: '', method: 'straight_line', salvage: '', openingAccum: '', openingAsOf: '',
  location: '', status: '', note: '', assetAccountId: '', accumAccountId: '', expenseAccountId: '',
};
type FaClass = { code: string; name_th: string; name_en: string; max_rate: number | null; depreciable: number };
/** The methods the engine computes (services/fixedAssets.ts METHODS) — one today, by the requirement. */
const METHODS = ['straight_line'];

/** What a category gives an asset: its rate (and so its life), method and the three accounts. */
const fromCategory = (c: FaCategory): Partial<Form> => {
  const r = c.nature !== 'none' && c.default_rate !== null && c.default_rate !== undefined && Number(c.default_rate) > 0 ? Number(c.default_rate) : 0;
  return {
    categoryId: c.id, rate: r ? String(r) : '', life: r ? fmtNum(100 / r, 4) : '', method: c.default_method || 'straight_line',
    assetAccountId: c.asset_account_id || '', accumAccountId: c.accum_account_id || '', expenseAccountId: c.expense_account_id || '',
  };
};

export function AssetForm({ ctx, assetId, sourceKey, onClose, onSaved }: { ctx: AwCtx; assetId: string; sourceKey: string; onClose: () => void; onSaved: (id: string) => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isEdit = !!assetId && assetId !== 'new';
  const srcMode = !isEdit && !!sourceKey;
  // ⛔ A read-only seat (and a closed period) gets no form at all — the window never offers one, and a
  // typed address must not either: the books login's every write is refused and pages the admins.
  const writable = canWriteIn(ctx);
  const [cats, setCats] = useState<FaCategory[] | null>(null);
  const [classes, setClasses] = useState<FaClass[]>([]);
  const [accts, setAccts] = useState<FaAcct[]>([]);
  const [statuses, setStatuses] = useState<string[]>(['']);
  const [asset, setAsset] = useState<(FaAsset & { removed?: boolean; deleted_by_name?: string | null; deleted_at?: number | null }) | null>(null);
  const [cadence, setCadence] = useState<'annual' | 'monthly' | ''>('');
  const [src, setSrc] = useState<FaSource | null>(null);
  /** EDIT of an asset bought on an Expense record: the line it was bought on, read as updateAsset reads it —
   *  'gone' when it is no longer there or no longer booked to an asset account; null when not bought. */
  const [boughtLine, setBoughtLine] = useState<FaSourceLine | 'gone' | null>(null);
  const [lineNo, setLineNo] = useState(0);
  const [f, setF] = useState<Form>(BLANK);
  const [init, setInit] = useState<Form | null>(null);
  const [by, setBy] = useState<'rate' | 'life'>('rate');
  const [loaded, setLoaded] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  /** The edit path: marks the form, so every exit asks first. */
  const ed = (patch: Partial<Form>) => { setDirty(true); setF(p => ({ ...p, ...patch })); };

  useEffect(() => {
    if (!writable) return;
    let alive = true;
    setLoadErr(''); setLoaded(false);
    Promise.all([
      api.faCategories(ctx.clientId), api.faClasses(), api.jnlAccounts(ctx.clientId), api.faAssetsPage(ctx.clientId, { page: 1, limit: 1 }),
      isEdit ? api.faAsset(ctx.clientId, assetId) : Promise.resolve(null),
      srcMode ? api.faSourceCandidates(ctx.clientId, sourceKey) : Promise.resolve(null),
      // The company's frequency: an edit has it in the asset's detail; a new asset asks for it alone.
      isEdit ? Promise.resolve(null) : api.faGetCadence(ctx.clientId).catch(() => null),
    ]).then(async ([c, k, ac, pg, det, sc, cd]: any[]) => {
      // ⛔ An edited asset bought on an Expense record: its line, as the server will check an edit against it.
      // A refused read (the record gone) is a line that is gone — the descriptive fields still save.
      const boughtKey = String((det as FaDetail | null)?.asset?.source_key || '');
      const bsc: FaSource | null = boughtKey ? await api.faSourceCandidates(ctx.clientId, boughtKey).catch(() => null) : null;
      if (!alive) return;
      const list: FaCategory[] = (c?.categories || []).filter((x: FaCategory) => Number(x.active) !== 0);
      setCats(list); setClasses(k?.classes || []); setAccts(ac?.accounts || []);
      setStatuses(((pg?.storedStatuses || ['']) as string[]).filter(s => s !== 'written_off'));
      const cad = String((det as FaDetail | null)?.cadence || cd?.cadence || '');
      setCadence(cad === 'monthly' || cad === 'annual' ? cad : '');
      // ⛔ Pre-fills never mark the form (FS-4): nothing has been typed yet.
      if (det) {
        const a: FaAsset = (det as FaDetail).asset;
        const v: Form = {
          categoryId: a.category_id || '', nameTh: a.name_th || '', nameEn: a.name_en || '', acquiredDate: a.acquired_date || '',
          vendorName: a.vendor_name || '', vendorInvoiceNo: a.vendor_invoice_no || '', cost: toInput(a.cost), capitalisedCost: toInput(a.capitalised_cost),
          availableDate: a.available_date || '', depreciationStart: a.depreciation_start || '',
          life: Number(a.useful_life_years) > 0 ? fmtNum(a.useful_life_years, 4) : '', rate: Number(a.rate) > 0 ? String(Number(a.rate)) : '',
          method: a.method || 'straight_line', salvage: toInput(a.salvage), openingAccum: toInput(a.opening_accum), openingAsOf: a.opening_as_of || '',
          location: a.location || '', status: a.status_stored || '', note: a.note || '',
          assetAccountId: a.asset_account_id || '', accumAccountId: a.accum_account_id || '', expenseAccountId: a.expense_account_id || '',
        };
        const bl = boughtKey ? (bsc?.lines || []).find(l => l.lineNo === Number(a.source_line_no)) : undefined;
        const usable = bl && bl.isAssetAccount && !!bl.accountId ? bl : null;
        setBoughtLine(boughtKey ? usable || 'gone' : null);
        // ⛔ (R5 #4) A line booked again since to another asset account: the asset follows it — updateAsset
        // refuses any other account the moment the cost or the account is saved. Shown, and sent, as a change.
        setAsset(a); setF(usable && !a.charged && usable.accountId !== v.assetAccountId ? { ...v, assetAccountId: usable.accountId } : v); setInit(v);
      }
      if (sc) {
        const s = sc as FaSource;
        setSrc(s);
        const usable = s.lines.filter(l => !l.why);
        if (usable.length === 1) { setLineNo(usable[0].lineNo); setF(p => ({ ...p, ...linePatch(usable[0], s, list, p) })); }
      }
      setLoaded(true);
    }).catch((e: any) => { if (alive) { setLoadErr(String(e?.message || e)); setLoaded(true); } });
    return () => { alive = false; };
  }, [ctx.clientId, assetId, sourceKey, writable]);   // eslint-disable-line react-hooks/exhaustive-deps

  /** A line of the record, as what it pre-fills. The category follows only when exactly one keeps its
   *  assets in the line's account and the one chosen does not. */
  function linePatch(l: FaSourceLine, s: FaSource, list: FaCategory[], cur: Form): Partial<Form> {
    const p: Partial<Form> = {
      cost: toInput(l.remaining), nameTh: l.description || cur.nameTh, vendorName: s.record?.supplier || cur.vendorName,
      vendorInvoiceNo: s.record?.supplierNo || cur.vendorInvoiceNo, acquiredDate: s.record?.date || cur.acquiredDate,
    };
    const match = list.filter(c => c.asset_account_id === l.accountId);
    const now = list.find(c => c.id === cur.categoryId);
    if (match.length === 1 && (!now || now.asset_account_id !== l.accountId)) Object.assign(p, fromCategory(match[0]));
    return p;
  }
  const pickLine = (l: FaSourceLine) => {
    if (l.why || !src) return;
    setLineNo(l.lineNo);
    const p = linePatch(l, src, cats || [], f);
    if (p.categoryId) setBy('rate');
    ed(p);
  };
  // ⛔ The account an EDITED asset bought on an Expense record must stay in: the account its line debits
  // (services/assetSource.ts) — updateAsset refuses any other. Its own, if the line could not be read.
  const bought = isEdit && !!init && !!String(asset?.source_key || '');
  const bLine = bought && boughtLine && boughtLine !== 'gone' ? boughtLine : null;
  const lineGone = bought && boughtLine === 'gone';
  const boughtAcct = !bought || !init ? '' : bLine ? bLine.accountId : init.assetAccountId;
  const pickCategory = (id: string) => {
    const c = (cats || []).find(x => x.id === id);
    setBy('rate');
    // A bought asset keeps its account whatever the category gives — sent with it, accepted, "set by hand".
    ed(c ? { ...fromCategory(c), ...(boughtAcct ? { assetAccountId: boughtAcct } : null) } : { categoryId: id });
  };
  // Life and rate are one setting: whichever is typed, the other follows (100 / n).
  // ⛔ (review R5 #1, residue) The asset's OWN life retyped, in its own category, is its own rate — never
  // 100 / n again: a 15% asset lives 6.6667 years, and 100 / 6.6667 is 14.9999, which showed a changed rate
  // marked "set by hand" while Save, rightly, had nothing to send. (So for 12%, 30%, 7%: any rate whose
  // life is not whole.)
  const ownLife = (v: string): boolean => !!init && v === init.life && f.categoryId === init.categoryId;
  // ⛔ (review R5 #1, residue on the other paths) …and the life the CATEGORY gave, typed back, is the
  // category's rate at full precision, worked by rate — so the rate is sent, never the 4-decimal life: a new
  // asset, a changed category, or an asset set by hand to another rate stored 6.6667 years as 14.999925%,
  // marked "set by hand", when the operator had only typed back what the form had filled in.
  const catRateOf = (v: string): number => {
    const r = cat && cat.nature !== 'none' ? Number(cat.default_rate) : 0;
    return r > 0 && v === fmtNum(100 / r, 4) ? r : 0;
  };
  const typeLife = (v: string) => {
    const own = !!init && ownLife(v);
    if (!own && catRateOf(v)) { setBy('rate'); ed({ life: v, rate: String(catRateOf(v)) }); return; }
    setBy('life'); ed({ life: v, rate: init && own ? init.rate : Number(v) > 0 ? fmtNum(100 / Number(v), 4) : '' });
  };
  const typeRate = (v: string) => { setBy('rate'); ed({ rate: v, life: Number(v) > 0 ? fmtNum(100 / Number(v), 4) : '' }); };

  const cat = (cats || []).find(c => c.id === f.categoryId) || null;
  const line = src?.lines.find(l => l.lineNo === lineNo) || null;
  /** The account the purchase put this cost in: the chosen line's (new), the bought asset's line's (edit) —
   *  or, its line gone, the bought asset's own. */
  const boughtRef = boughtAcct ? accts.find(a => a.id === boughtAcct) : undefined;
  const buy: { id: string; code: string; name: string } | null = line ? { id: line.accountId, code: line.accountCode, name: line.accountName }
    : bLine ? { id: bLine.accountId, code: bLine.accountCode, name: bLine.accountName }
    : boughtAcct ? { id: boughtAcct, code: boughtRef?.code || '', name: boughtRef ? pickName(lang, boughtRef.name_th, boughtRef.name_en) : '' } : null;
  // ⛔ NEW from a record: the asset must sit where the line put the cost — anywhere else and the server
  // refuses it (checkedSource), so Register is held and the form says why (a refused write pages the
  // admins). ⛔ Never in EDIT (review R5 #4): there the select is held to the line's account and SENT with
  // every new category, so a category keeping its assets elsewhere is accepted — the account is then only
  // "set by hand" (handOf), and holding Save, with "the register will refuse it", was untrue.
  const acctHeld = !isEdit && !!buy && f.assetAccountId !== buy.id;
  // …and the sentence says where that account came from: "this category puts the asset in …" only when
  // the category did; set by hand (or no category yet), it says so and asks for the purchase's account —
  // the category chosen may well be the one keeping its assets there (review R5 #2, residue).
  const acctFromCat = !!cat && (cat.asset_account_id || '') === f.assetAccountId;
  // ⛔ (review R5 #5) What the line has left for this asset: the line less every OTHER asset bought on it —
  // the COST ALONE, as registration and updateAsset both count it. A cost moved past it is refused (409).
  const costRoom = bLine ? bLine.remaining + Math.round(Number(asset?.source_amount) || 0) : 0;
  const costOver = !!bLine && !!init && !asset?.charged && toSatang(f.cost) !== toSatang(init.cost) && toSatang(f.cost) > costRoom;
  const classCode = cat ? cat.decree_class_code : (asset && f.categoryId === (init?.categoryId ?? '') ? asset.class_code : '');
  const cls = classes.find(c => c.code === classCode) || null;
  const depreciable = cls ? Number(cls.depreciable) !== 0 && (!cat || cat.nature !== 'none') : !(cat && cat.nature === 'none');
  const disposed = !!asset?.disposed_date;
  // Removed (soft-deleted): it opens in its window read-only; there is nothing here to save.
  const removed = !!asset?.removed;
  // ⛔ Once charged, the ledger's charges were computed from these — the server refuses them (BOUND_FIELDS).
  const locked = !!asset?.charged;
  const ov = new Set(asset?.overrides || []);
  // "Set by hand": the same comparison the server stores (services/fixedAssets.ts overridesOf), against
  // the category chosen in the form; an edit whose category is not in the list keeps the stored marks.
  // The rate compared is the one the server will STORE: a typed life becomes rateFromLife (6 decimals).
  // ⛔ …and the asset's own life, retyped, is sent as nothing — its stored rate stays (R5 #1 residue, above).
  const storedRate = (): number => (by === 'life' && init && ownLife(f.life) ? Number(init.rate)
    : by === 'life' && Number(f.life) > 0 ? Math.round((100 / Number(f.life)) * 1e6) / 1e6 : Number(f.rate));
  const handOf = (k: 'rate' | 'method' | 'assetAccount' | 'accumAccount' | 'expenseAccount'): boolean => {
    if (!cat) return isEdit && !!init && f.categoryId === init.categoryId && ov.has(k);
    if (k === 'rate') return cat.nature !== 'none' && cat.default_rate !== null && cat.default_rate !== undefined && f.rate !== '' && Math.abs(storedRate() - Number(cat.default_rate)) > 1e-9;
    if (k === 'method') return (f.method || 'straight_line') !== (cat.default_method || 'straight_line');
    if (k === 'assetAccount') return f.assetAccountId !== (cat.asset_account_id || '');
    if (cat.nature === 'none') return false;
    return k === 'accumAccount' ? f.accumAccountId !== (cat.accum_account_id || '') : f.expenseAccountId !== (cat.expense_account_id || '');
  };
  const openingAmt = toSatang(f.openingAccum);
  // Both or neither — checked where it is being set: a new asset, or an edit that touches either half.
  const openingTouched = !isEdit || (!!init && (f.openingAccum !== init.openingAccum || f.openingAsOf !== init.openingAsOf));
  const openingOk = !openingTouched || (openingAmt > 0) === !!f.openingAsOf;
  /** What the asset starts this system at: its total cost less the figure brought forward (satang). */
  const openingNbv = toSatang(f.cost) + toSatang(f.capitalisedCost) - openingAmt;

  /** EDIT: exactly the fields that changed, each under the name PUT /assets/:id knows it by. */
  const patchOf = (): Record<string, any> => {
    if (!init) return {};
    const p: Record<string, any> = {};
    for (const k of ['nameTh', 'nameEn', 'vendorName', 'vendorInvoiceNo', 'location', 'note'] as const) if (f[k].trim() !== init[k].trim()) p[k] = f[k].trim();
    if (f.status !== init.status) p.status = f.status;
    if (!locked) {
      // ⛔ A new category: the server fills every setting the patch leaves out FROM THAT CATEGORY, so each
      // one is sent as the form now holds it — compared with `init` it could look unchanged (an account
      // set back to the old one, a rate typed equal to the old one) and the category's would be saved.
      const recat = f.categoryId !== init.categoryId;
      if (recat) p.categoryId = f.categoryId;
      for (const k of ['acquiredDate', 'availableDate', 'depreciationStart', 'openingAsOf', 'method', 'assetAccountId', 'accumAccountId', 'expenseAccountId'] as const) {
        const fromCat = recat && (k === 'method' || k === 'assetAccountId' || k === 'accumAccountId' || k === 'expenseAccountId');
        if (fromCat || f[k] !== init[k]) p[k] = f[k];
      }
      for (const k of ['cost', 'capitalisedCost', 'salvage', 'openingAccum'] as const) if (toSatang(f[k]) !== toSatang(init[k])) p[k] = toSatang(f[k]);
      if (depreciable) {
        // The life OR the rate — whichever was typed last — never both (the server derives the other).
        // ⛔ (review R5 #1) Working by life, the rate is never the fallback: the rate box then holds 100 / n to
        // 4 decimals, so a life retyped to its own value (3 → 4 → 3) sent 33.3333 for a stored 33.333333 —
        // the rate drifted and was marked "set by hand", with Save lit though nothing on screen had changed.
        if (by === 'life') { if ((recat || f.life !== init.life) && Number(f.life) > 0) p.usefulLifeYears = Number(f.life); }
        else if ((recat || f.rate !== init.rate) && Number(f.rate) > 0) p.rate = Number(f.rate);
      }
    }
    return p;
  };
  /** NEW: the whole asset. Amounts in satang; the life OR the rate, whichever was typed. */
  const bodyOf = (): Record<string, any> => {
    const b: Record<string, any> = {
      categoryId: f.categoryId, nameTh: f.nameTh.trim(), nameEn: f.nameEn.trim(), acquiredDate: f.acquiredDate,
      vendorName: f.vendorName.trim(), vendorInvoiceNo: f.vendorInvoiceNo.trim(),
      cost: toSatang(f.cost), capitalisedCost: toSatang(f.capitalisedCost), salvage: toSatang(f.salvage),
      availableDate: f.availableDate, depreciationStart: f.depreciationStart, method: f.method,
      openingAccum: openingAmt, openingAsOf: f.openingAsOf, location: f.location.trim(), status: f.status, note: f.note.trim(),
      assetAccountId: f.assetAccountId, accumAccountId: depreciable ? f.accumAccountId : '', expenseAccountId: depreciable ? f.expenseAccountId : '',
    };
    if (depreciable) {
      if (by === 'life' && Number(f.life) > 0) b.usefulLifeYears = Number(f.life);
      else if (Number(f.rate) > 0) b.rate = Number(f.rate);
    }
    if (srcMode) { b.sourceKey = sourceKey; b.sourceLineNo = lineNo; }
    return b;
  };
  const changed = isEdit ? Object.keys(patchOf()) : [];
  const srcBlocked = !!src && !src.lines.some(l => !l.why);
  const formShown = writable && loaded && !loadErr && !(isEdit && (!asset || disposed || removed)) && !(srcMode && (!src || srcBlocked));
  const ready = formShown && openingOk && !acctHeld && !costOver && !!f.nameTh.trim() && (isEdit
    // (an asset that has a category cannot be saved into none — the server refuses that)
    ? changed.length > 0 && (!!f.categoryId || f.categoryId === init?.categoryId)
    : !!f.categoryId && isoDate(f.acquiredDate) && toSatang(f.cost) > 0 && (!srcMode || lineNo > 0));
  const save = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      // Saved: nothing is unsaved any more, whether or not the host closes the form at once.
      if (isEdit) { await api.faUpdate(ctx.clientId, assetId, patchOf()); setDirty(false); setBusy(false); ctx.bump(); onSaved(assetId); }
      else { const r: any = await api.faRegister(ctx.clientId, bodyOf()); setDirty(false); setBusy(false); ctx.bump(); onSaved(String(r?.id || '')); }
    } catch (e: any) { setErr(String(e?.message || e)); setBusy(false); }
  };

  // ── drawing ──────────────────────────────────────────────────────────────────────────────────
  const lockStyle: React.CSSProperties = { background: 'var(--bg-app)', color: 'var(--text-tertiary)' };
  const box = (k: keyof Form, label: string, o: { type?: string; lock?: boolean; hand?: boolean; step?: string; req?: boolean; hint?: React.ReactNode; set?: (v: string) => void; ph?: string } = {}) => (
    <div key={k} style={{ minWidth: 0 }}>
      <label style={{ ...lbl, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>{label}{o.req ? ' *' : ''}{o.hand && <SetByHand t={t} />}</label>
      <input data-f={k} type={o.type || 'text'} value={f[k]} disabled={!!o.lock} step={o.step} min={o.type === 'number' ? '0' : undefined} placeholder={o.ph}
        onChange={e => (o.set ? o.set(e.target.value) : ed({ [k]: e.target.value } as Partial<Form>))} style={{ ...field, ...(o.lock ? lockStyle : null) }} />
      {o.hint ? <div style={{ ...note, marginTop: 3 }}>{o.hint}</div> : null}
    </div>
  );
  const acctOf = (id: string) => accts.find(a => a.id === id);
  const acctSel = (k: 'assetAccountId' | 'accumAccountId' | 'expenseAccountId', label: string, list: FaAcct[], hand: boolean, catAcct: string) => {
    const opts = list.some(a => a.id === f[k]) || !f[k] ? list : [...list, ...accts.filter(a => a.id === f[k])];
    const fromCat = accts.find(a => a.id === catAcct);
    // ⛔ A bought asset's account — its line's: locked, with why (review R5 #2/#4) — any other is refused by the server.
    const kept = k === 'assetAccountId' && !!boughtAcct;
    const off = locked || kept;
    // the line booked again since to another asset account: the asset follows it (set at load)
    const was = kept && !!bLine && !!init && init.assetAccountId !== bLine.accountId ? acctOf(init.assetAccountId) : undefined;
    return (
      <div key={k} style={{ minWidth: 0 }}>
        <label style={{ ...lbl, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>{label}{hand && <SetByHand t={t} />}</label>
        <select data-f={k} value={f[k]} disabled={off} onChange={e => ed({ [k]: e.target.value } as Partial<Form>)} style={{ ...field, ...(off ? lockStyle : null) }}>
          <option value="">{t('aw_faa_choose')}</option>
          {opts.map(a => <option key={a.id} value={a.id}>{acctLabel(lang, a)}</option>)}
        </select>
        {kept && !locked && (
          <div data-why={lineGone ? 'line-gone' : 'bought'} style={{ ...note, marginTop: 3, ...(lineGone || was ? { color: '#92400E' } : null) }}>
            {lineGone ? t('aw_faa_src_line_gone')
              : bLine && was ? t('aw_faa_src_acct_moved', { code: bLine.accountCode, name: bLine.accountName, was: acctLabel(lang, was) })
              : t('aw_faa_src_acct_kept')}
          </div>
        )}
        {cat && <div style={{ ...note, marginTop: 3 }}>{t('aw_faa_cat_account', { acct: fromCat ? acctLabel(lang, fromCat) : '—' })}</div>}
      </div>
    );
  };
  const grid: React.CSSProperties = { ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px 16px' };
  const catOpt = (c: FaCategory) => <option key={c.id} value={c.id}>{pickName(lang, c.name_th, c.name_en)}</option>;
  const catList: FaCategory[] = (() => {
    const list = cats || [];
    if (!asset || !asset.category_id || list.some(c => c.id === asset.category_id)) return list;
    // An edit whose category was switched off since: still named, so the select does not read blank.
    return [...list, { id: asset.category_id, name_th: asset.category_th, name_en: asset.category_en } as FaCategory];
  })();
  const matching = buy ? catList.filter(c => c.asset_account_id === buy.id) : [];
  const others = buy ? catList.filter(c => c.asset_account_id !== buy.id) : catList;
  const statusOpts = statuses.includes(f.status) ? statuses : [...statuses, f.status];

  const title = isEdit ? t('aw_faa_form_edit_title') : srcMode ? t('aw_faa_form_src_title') : t('aw_faa_form_new_title');
  const footer = formShown
    ? <Foot onClose={onClose} onSubmit={save} busy={busy} ready={ready} icon={isEdit ? 'ti ti-device-floppy' : 'ti ti-plus'} label={isEdit ? t('aw_faa_save_btn') : t('aw_faa_register_btn')} t={t} />
    : <div style={{ display: 'flex', justifyContent: 'flex-end' }}><CloseBtn onClose={onClose} t={t} /></div>;

  return (
    <Dialog fullscreen title={title} docNo={isEdit ? asset?.asset_no : undefined} onClose={onClose} busy={busy} dirty={dirty} footer={footer}>
      <div data-fa-form={isEdit ? 'edit' : srcMode ? 'source' : 'new'} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!writable && <div style={{ ...card, ...note }}><i className="ti ti-lock" aria-hidden /> {isReadOnlySeat(ctx) ? t('aw_faa_form_readonly') : t('aw_period_closed_msg')}</div>}
        {writable && !loaded && <Loader />}
        {loadErr && <div style={errBox}>{loadErr}</div>}
        {writable && loaded && isEdit && removed && (
          <div data-why="removed" style={{ ...card, color: '#B42318', fontSize: 12.5 }}>
            <i className="ti ti-trash" aria-hidden /> {t('aw_faa_removed_banner', { by: asset?.deleted_by_name || '—', when: fmtDateTime(asset?.deleted_at) || '—' })}
          </div>
        )}
        {writable && loaded && isEdit && disposed && !removed && (
          <div data-why="disposed" style={{ ...card, color: '#92400E', fontSize: 12.5 }}><i className="ti ti-info-circle" aria-hidden /> {t('aw_faa_disposed_no_edit', { date: dmy(String(asset?.disposed_date)) })}</div>
        )}

        {/* COA-H7: the Expense record this asset is bought on, and its lines as choices. */}
        {writable && loaded && src && (
          <div>
            <div style={sectionHead}>{t('aw_faa_src_title')}</div>
            <div data-fa-src style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {src.record && (
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5 }}>
                  <span><span style={note}>{t('aw_faa_src_no')}</span> <b style={{ fontVariantNumeric: 'tabular-nums' }}>{src.record.expenseNo || src.record.supplierNo || '—'}</b></span>
                  <span><span style={note}>{t('aw_faa_f_supplier')}</span> <b>{src.record.supplier || '—'}</b></span>
                  <span><span style={note}>{t('aw_faa_f_invoice_no')}</span> {src.record.supplierNo || '—'}</span>
                  <span><span style={note}>{t('aw_faa_src_date')}</span> {src.record.date ? dmy(src.record.date) : '—'}</span>
                </div>
              )}
              {srcBlocked && src.refusal && <div data-refusal={src.refusal.code} style={errBox}>{src.refusal.why}</div>}
              {!srcBlocked && !!src.lines.length && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={table} data-fa-lines>
                    <thead><tr>
                      <th style={th} aria-label={t('aw_faa_src_pick')} />
                      <th style={th}>{t('aw_faa_src_line')}</th>
                      <th style={th}>{t('aw_faa_src_desc')}</th>
                      <th style={th}>{t('aw_faa_src_account')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_faa_src_amount')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_faa_src_registered')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_faa_src_remaining')}</th>
                    </tr></thead>
                    <tbody>
                      {src.lines.map(l => (
                        <tr key={l.lineNo} data-line={l.lineNo} style={l.why ? { opacity: 0.7 } : lineNo === l.lineNo ? { background: 'var(--accent-bg)' } : undefined}>
                          <td style={td}>
                            <input type="radio" name={`faa-line-${sourceKey}`} data-f={`line-${l.lineNo}`} disabled={!!l.why} checked={lineNo === l.lineNo}
                              onChange={() => pickLine(l)} aria-label={t('aw_faa_src_line_n', { n: l.lineNo })} />
                          </td>
                          <td style={td}>{l.lineNo}</td>
                          <td style={td}>{l.description || '—'}{l.why && <div data-why={l.lineNo} style={{ ...note, color: '#92400E' }}>{l.why}</div>}</td>
                          <td style={td}>{l.accountCode || '—'}{l.accountName ? ` · ${l.accountName}` : ''}</td>
                          <td style={num}>{baht(l.amount)}</td>
                          <td style={num}>{baht(l.registered)}</td>
                          <td style={num}>{baht(l.remaining)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!srcBlocked && <div style={note}>{t('aw_faa_src_hint')}</div>}
            </div>
          </div>
        )}

        {formShown && (
          <>
            {isEdit && locked && (
              <div data-why="locked" style={{ ...card, padding: '10px 12px', fontSize: 12.5, color: '#92400E' }}>
                <i className="ti ti-lock" aria-hidden /> {t('aw_faa_locked_why')}
              </div>
            )}
            <div>
              <div style={sectionHead}>{t('aw_faa_sec_asset')}</div>
              <div style={grid}>
                <div style={{ minWidth: 0 }}>
                  <label style={lbl}>{t('aw_faa_f_category')} *</label>
                  <select data-f="categoryId" value={f.categoryId} disabled={locked} onChange={e => pickCategory(e.target.value)} style={{ ...field, ...(locked ? lockStyle : null) }}>
                    <option value="">{t('aw_faa_choose_category')}</option>
                    {buy ? (
                      <>
                        {!!matching.length && <optgroup label={t('aw_faa_cat_matching', { code: buy.code })}>{matching.map(catOpt)}</optgroup>}
                        {!!others.length && <optgroup label={t('aw_faa_cat_others')}>{others.map(catOpt)}</optgroup>}
                      </>
                    ) : catList.map(catOpt)}
                  </select>
                  <div style={{ ...note, marginTop: 3 }}>
                    {cls ? (Number(cls.depreciable) && depreciable
                      ? t('aw_faa_class_line', { cls: pickName(lang, cls.name_th, cls.name_en), max: fmtNum(cls.max_rate) })
                      : t('aw_faa_class_not_dep', { cls: pickName(lang, cls.name_th, cls.name_en) }))
                      : t('aw_faa_category_hint')}
                  </div>
                  {cats !== null && !cats.length && <div style={{ ...note, color: '#92400E' }}>{t('aw_faa_no_categories')}</div>}
                </div>
                {box('nameTh', t('aw_faa_f_name_th'), { req: true })}
                {box('nameEn', t('aw_faa_f_name_en'))}
              </div>
              {buy && acctHeld && (
                <div data-warn="account" style={{ ...card, marginTop: 8, padding: '9px 12px', fontSize: 12.5, color: '#92400E' }}>
                  <i className="ti ti-alert-triangle" aria-hidden /> {t(acctFromCat ? 'aw_faa_src_acct_differs' : 'aw_faa_src_acct_hand', {
                    have: acctOf(f.assetAccountId) ? acctLabel(lang, acctOf(f.assetAccountId)) : '—', code: buy.code, name: buy.name,
                  })}
                </div>
              )}
              {line && !matching.length && <div style={{ ...note, marginTop: 4 }}>{t('aw_faa_cat_none_matching', { code: line.accountCode })}</div>}
            </div>

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_purchase')}</div>
              <div style={grid}>
                {box('acquiredDate', t('aw_faa_f_purchase_date'), { type: 'date', lock: locked, req: true })}
                {box('vendorName', t('aw_faa_f_supplier'))}
                {box('vendorInvoiceNo', t('aw_faa_f_invoice_no'))}
                {box('cost', t('aw_faa_f_cost'), { type: 'number', step: '0.01', lock: locked || lineGone, req: true, ph: '0.00',
                  hint: costOver && bLine ? <span data-warn="cost" style={{ color: '#92400E' }}>{t('aw_faa_src_cost_over', { n: bLine.lineNo, amount: baht(bLine.amount), max: baht(costRoom) })}</span>
                    : lineGone && !locked ? <span data-why="line-gone">{t('aw_faa_src_line_gone')}</span> : undefined })}
                {box('capitalisedCost', t('aw_faa_f_capitalised'), { type: 'number', step: '0.01', lock: locked, ph: '0.00', hint: t('aw_faa_capitalised_hint') })}
                {box('salvage', t('aw_faa_f_residual'), { type: 'number', step: '0.01', lock: locked, ph: '0.00' })}
              </div>
            </div>

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_depreciation')}</div>
              <div style={grid}>
                {box('availableDate', t('aw_faa_f_available_date'), { type: 'date', lock: locked })}
                {box('depreciationStart', t('aw_faa_f_start_date'), { type: 'date', lock: locked,
                  hint: f.depreciationStart ? undefined : f.availableDate ? t('aw_faa_start_is_available', { date: dmy(f.availableDate) })
                    : f.acquiredDate ? t('aw_faa_start_is_purchase', { date: dmy(f.acquiredDate) }) : t('aw_faa_start_hint') })}
                {depreciable && box('life', t('aw_faa_f_life'), { type: 'number', step: 'any', lock: locked, set: typeLife, hand: handOf('rate'), hint: t('aw_faa_life_hint') })}
                {depreciable && box('rate', t('aw_faa_f_rate'), { type: 'number', step: 'any', lock: locked, set: typeRate, hand: handOf('rate'),
                  hint: cls && cls.max_rate !== null ? t('aw_faa_ceiling', { max: fmtNum(cls.max_rate), cls: pickName(lang, cls.name_th, cls.name_en) }) : undefined })}
                {depreciable && (
                  <div style={{ minWidth: 0 }}>
                    <label style={{ ...lbl, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>{t('aw_faa_f_method')}{handOf('method') && <SetByHand t={t} />}</label>
                    <select data-f="method" value={f.method} disabled={locked} onChange={e => ed({ method: e.target.value })} style={{ ...field, ...(locked ? lockStyle : null) }}>
                      {METHODS.map(m => <option key={m} value={m}>{t(METHOD_KEY[m])}</option>)}
                    </select>
                    <div style={{ ...note, marginTop: 3 }}>{t('aw_faa_method_hint')}</div>
                    {!!cadence && (
                      <div data-frequency={cadence} style={{ ...note, marginTop: 3 }}>
                        {t('aw_fad_freq_is', { freq: t(cadence === 'monthly' ? 'aw_fad_freq_monthly' : 'aw_fad_freq_annual') })}
                      </div>
                    )}
                  </div>
                )}
                {!depreciable && <div style={{ ...note, gridColumn: '1 / -1' }}>{t('aw_faa_not_depreciated')}</div>}
              </div>
            </div>

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_opening')}</div>
              <div style={grid}>
                {box('openingAccum', t('aw_faa_f_opening'), { type: 'number', step: '0.01', lock: locked, ph: '0.00' })}
                {box('openingAsOf', t('aw_faa_f_opening_as_of'), { type: 'date', lock: locked })}
                <div style={{ ...note, gridColumn: '1 / -1', color: openingOk ? undefined : '#92400E' }} data-why={openingOk ? undefined : 'opening'}>
                  {openingOk ? t('aw_faa_opening_hint') : t('aw_faa_opening_both')}
                </div>
                {openingAmt > 0 && (
                  <div data-opening-nbv style={{ ...note, gridColumn: '1 / -1', fontWeight: 700, color: openingNbv < 0 ? '#92400E' : 'var(--text-secondary)' }}>
                    {t('aw_faa_opening_nbv', { amount: baht(openingNbv) })}
                  </div>
                )}
              </div>
            </div>

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_where')}</div>
              <div style={grid}>
                {box('location', t('aw_faa_f_location'))}
                <div style={{ minWidth: 0 }}>
                  <label style={lbl}>{t('aw_faa_f_status')}</label>
                  <select data-f="status" value={f.status} onChange={e => ed({ status: e.target.value })} style={field}>
                    {statusOpts.map(s => <option key={s || 'auto'} value={s}>{s && STATUS_KEY[s as FaStatus] ? t(STATUS_KEY[s as FaStatus]) : t('aw_faa_status_auto')}</option>)}
                  </select>
                  <div style={{ ...note, marginTop: 3 }}>{t('aw_faa_status_hint')}</div>
                </div>
                <div style={{ minWidth: 0, gridColumn: '1 / -1' }}>
                  <label style={lbl}>{t('aw_faa_f_note')}</label>
                  <textarea data-f="note" value={f.note} onChange={e => ed({ note: e.target.value })} rows={2} maxLength={1000} style={{ ...field, resize: 'vertical' }} />
                </div>
              </div>
            </div>

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_accounts')}</div>
              <div style={grid}>
                {acctSel('assetAccountId', t('aw_faa_acct_asset'), accts.filter(a => a.class === 'asset'), handOf('assetAccount'), cat?.asset_account_id || '')}
                {depreciable && acctSel('accumAccountId', t('aw_faa_acct_accum'), accts.filter(a => a.class === 'asset'), handOf('accumAccount'), cat?.accum_account_id || '')}
                {depreciable && acctSel('expenseAccountId', t('aw_faa_acct_expense'), accts.filter(a => a.class === 'expense'), handOf('expenseAccount'), cat?.expense_account_id || '')}
              </div>
              <div style={{ ...note, marginTop: 4 }}>{t('aw_faa_accounts_hint')}</div>
            </div>
            {!isEdit && !f.categoryId && <div data-why="category" style={{ ...note, color: '#92400E' }}>{t('aw_faa_category_required')}</div>}
          </>
        )}
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}
